import { ArrowRightIcon, Checkbox, Chip } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FindingSlotHighlight } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { getWarningTypeTranslation, type WarningType } from '../../../utils/getWarningTypeTranslation';
import type { NormalizedFinding } from '../../../utils/timetable/normalizedFinding';
import { priorityToSeverity, severityToken } from '../../../utils/timetable/severity';
import { cleanConstraintLabel } from '../../../utils/timetable/timefoldConstraints';
import { useTimetableStore } from '../TimetableProvider';
import styles from './FindingsPanel.module.scss';

type FindingsItemProps = {
  finding: NormalizedFinding;
  mode: 'control' | 'timefold' | null;
};

export const FindingsItem = observer(({ finding, mode }: FindingsItemProps) => {
  const { t } = useTranslation();
  const store = useTimetableStore();
  const [expand, setExpand] = useState(false);

  useEffect(() => {
    setExpand(mode === 'timefold' ? store.timefoldAnalyzeMode.expand : store.controlMode.expand);
  }, [store.controlMode.expand, store.timefoldAnalyzeMode.expand, mode]);

  const token = severityToken(priorityToSeverity(finding.severity));

  // All ready strings come from the backend: timefold findings carry title/explanation/actionHint
  // (cleaned label fallback); control findings carry text + actionHint. The control title prefers
  // the warning's resolved text, then the warning-type translation.
  const isTimefold = finding.source === 'timefold';
  const title = isTimefold
    ? (finding.title ?? cleanConstraintLabel(finding.text))
    : finding.text
      ? t(finding.text, { defaultValue: finding.text })
      : getWarningTypeTranslation(finding.rawTypes[0] as WarningType);
  const explanation = isTimefold ? (finding.explanation ?? '') : '';
  const actionHint = finding.actionHint ?? '';

  // Slot-Highlights: jede Slot-Referenz landet in der Zeile ihrer Klasse UND in den Zeilen der
  // betroffenen Ressourcen — je nach Board-Kontext greift die passende (reuse des Zell-Highlights).
  const slotHighlights: FindingSlotHighlight[] = finding.slots.flatMap((slot) => {
    const rowUuids = new Set<string>([
      ...(slot.classUuid ? [slot.classUuid] : []),
      ...finding.involved.map((resource) => resource.uuid),
    ]);
    return [...rowUuids].map((rowUuid) => ({
      rowUuid,
      hourUuid: slot.timegridEntryUuid,
      day: slot.weekday as FindingSlotHighlight['day'],
      hint: title,
    }));
  });

  const canHighlight = finding.cardUuids.length > 0 || slotHighlights.length > 0;

  const toggleHighlight = (checked: boolean) => {
    if (checked) {
      store.addFindingHighlights(finding.cardUuids);
      if (slotHighlights.length > 0) store.addFindingSlotHighlights(slotHighlights);
      // Scroll the board to the finding's location — only one of these rows is rendered per context (P1-7).
      const rowCandidates = [
        ...new Set([
          ...slotHighlights.map((slot) => slot.rowUuid),
          ...finding.involved.map((resource) => resource.uuid),
        ]),
      ];
      if (rowCandidates.length > 0) store.setScrollToRows(rowCandidates);
    } else {
      store.removeFindingHighlights(finding.cardUuids);
      if (slotHighlights.length > 0) store.removeFindingSlotHighlights(slotHighlights);
    }
  };

  return (
    <div className={classNames(styles['finding-item'], { [styles.open]: expand })}>
      <div className={styles.headline} onClick={() => setExpand(!expand)}>
        <ArrowRightIcon className={classNames(styles.indicator, 'small')} />
        <token.Icon className='small' color={token.color} />
        <div className={styles.text}>{title}</div>
        <div className={styles.count}>{finding.cardUuids.length}x</div>
        <div className={styles.toggle} onClick={(event) => event.stopPropagation()}>
          <Checkbox
            disabled={!canHighlight}
            tooltipText={!canHighlight ? t('findings.noBoardLink') : undefined}
            name={`finding-${finding.key}`}
            onChange={(event) => toggleHighlight(event.target.checked)}
          />
        </div>
      </div>
      {expand && (
        <div className={styles.detail}>
          {explanation ? <div className={styles.note}>{explanation}</div> : null}
          {actionHint ? <div className={styles['action-hint']}>{actionHint}</div> : null}
          {finding.involved.length > 0 ? (
            <div className={styles.involved}>
              {finding.involved.map((resource) => (
                <Chip
                  key={`${resource.type}:${resource.uuid}`}
                  value={`${resource.label || resource.uuid.slice(0, 6)}${resource.typeLabel ? ` · ${resource.typeLabel}` : ''}`}
                  color='var(--color-grey)'
                  bgColor='var(--color-grey-lighter)'
                />
              ))}
            </div>
          ) : (
            <div className={styles.note}>{t('findings.cardCount', { count: finding.cardUuids.length })}</div>
          )}
        </div>
      )}
    </div>
  );
});
